import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { 
  createNotification, 
  getAllNotifications, 
  updateNotification, 
  archiveNotification, 
  deleteNotification
} from '../../services/notificationService';
import { Timestamp } from 'firebase/firestore';
import { FaPlus, FaBell, FaEdit, FaArchive, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';

const PageContainer = styled.div`
  padding: 16px;
  
  @media (min-width: 768px) {
    padding: 24px;
  }
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
  
  @media (min-width: 768px) {
    font-size: 24px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
  width: 100%;
  
  @media (min-width: 768px) {
    width: auto;
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
  
  svg {
    margin-right: 8px;
  }
`;

const NotificationsTable = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background-color: white;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;
`;

const TableHeader = styled.th`
  text-align: left;
  padding: 12px 16px;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textDark};
  font-weight: 600;
  white-space: nowrap;
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.colors.backgroundLight};
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.backgroundHover};
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme, $color }) => theme.colors[$color] || theme.colors.primary};
  margin-right: 8px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const PriorityBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${({ $priority, theme }) => {
    switch ($priority) {
      case 'high':
        return theme.colors.danger + '20';
      case 'medium':
        return theme.colors.warning + '20';
      default:
        return theme.colors.success + '20';
    }
  }};
  color: ${({ $priority, theme }) => {
    switch ($priority) {
      case 'high':
        return theme.colors.danger;
      case 'medium':
        return theme.colors.warning;
      default:
        return theme.colors.success;
    }
  }};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${({ $status, theme }) => 
    $status === 'active' ? theme.colors.success + '20' : theme.colors.textLight + '20'};
  color: ${({ $status, theme }) => 
    $status === 'active' ? theme.colors.success : theme.colors.textLight};
`;

const TargetBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  margin-right: 4px;
  background-color: ${({ theme }) => theme.colors.primary + '20'};
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 4px;
`;

const TargetContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textLight};
  
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: 16px;
  overflow-y: auto;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const Checkbox = styled.input`
  margin-right: 8px;
`;

const ModalFooter = styled.div`
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  
  background-color: ${({ $primary, theme }) => 
    $primary ? theme.colors.primary : 'transparent'};
  color: ${({ $primary, theme }) => 
    $primary ? 'white' : theme.colors.text};
  border: ${({ $primary, theme }) => 
    $primary ? 'none' : `1px solid ${theme.colors.border}`};
    
  &:hover {
    background-color: ${({ $primary, theme }) => 
      $primary ? theme.colors.primaryDark : theme.colors.backgroundLight};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const NotificationManagement = () => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    priority: 'medium',
    targetGroups: [],
    expiresAt: '',
    actionURL: '',
    imageURL: ''
  });
  const [editingId, setEditingId] = useState(null);

  // Fetch all notifications on component mount
  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const allNotifications = await getAllNotifications();
      setNotifications(allNotifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNotificationForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setNotificationForm(prev => {
      if (checked) {
        return {
          ...prev,
          targetGroups: [...prev.targetGroups, value]
        };
      } else {
        return {
          ...prev,
          targetGroups: prev.targetGroups.filter(group => group !== value)
        };
      }
    });
  };

  const resetForm = () => {
    setNotificationForm({
      title: '',
      message: '',
      priority: 'medium',
      targetGroups: [],
      expiresAt: '',
      actionURL: '',
      imageURL: ''
    });
    setEditingId(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (notification) => {
    // Convert Firestore timestamp to input date-time format
    const expiresAt = notification.expiresAt?.toDate();
    const formattedExpiresAt = expiresAt 
      ? new Date(expiresAt.getTime() - expiresAt.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16) 
      : '';
    
    setNotificationForm({
      title: notification.title || '',
      message: notification.message || '',
      priority: notification.priority || 'medium',
      targetGroups: notification.targetGroups || [],
      expiresAt: formattedExpiresAt,
      actionURL: notification.actionURL || '',
      imageURL: notification.imageURL || ''
    });
    setEditingId(notification.id);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const { title, message, priority, targetGroups, expiresAt, actionURL, imageURL } = notificationForm;
      
      // Validation
      if (!title || !message || targetGroups.length === 0 || !expiresAt) {
        return toast.error('Please fill in all required fields');
      }
      
      const notificationData = {
        title,
        message,
        priority,
        targetGroups,
        expiresAt: Timestamp.fromDate(new Date(expiresAt)),
        actionURL: actionURL || null,
        imageURL: imageURL || null,
        createdBy: currentUser.uid
      };
      
      if (editingId) {
        // Update existing notification
        await updateNotification(editingId, notificationData);
        toast.success('Notification updated successfully');
      } else {
        // Create new notification
        await createNotification(notificationData);
        toast.success('Notification created successfully');
      }
      
      closeModal();
      fetchNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
      toast.error('Failed to save notification');
    }
  };

  const handleArchive = async (notificationId) => {
    try {
      await archiveNotification(notificationId);
      toast.success('Notification archived successfully');
      fetchNotifications();
    } catch (error) {
      console.error('Error archiving notification:', error);
      toast.error('Failed to archive notification');
    }
  };

  const handleDelete = async (notificationId) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await deleteNotification(notificationId);
        toast.success('Notification deleted successfully');
        fetchNotifications();
      } catch (error) {
        console.error('Error deleting notification:', error);
        toast.error('Failed to delete notification');
      }
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    // Convert Firestore timestamp to JS Date
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return format(date, 'MMM d, yyyy h:mm a');
  };
  
  return (
    <PageContainer>
      <PageHeader>
        <Title>Notification Management</Title>
        <AddButton onClick={openAddModal}>
          <FaPlus />
          Add Notification
        </AddButton>
      </PageHeader>
      
      
      
      {loading ? (
        <LoadingState>Loading notifications...</LoadingState>
      ) : notifications.length === 0 ? (
        <EmptyState>No notifications found. Create one to get started.</EmptyState>
      ) : (
        <NotificationsTable>
          <Table>
            <thead>
              <tr>
                <TableHeader>Title</TableHeader>
                <TableHeader>Priority</TableHeader>
                <TableHeader>Target Groups</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Expires</TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {notifications.map((notification) => (
                <TableRow key={notification.id}>
                  <TableCell>{notification.title}</TableCell>
                  <TableCell>
                    <PriorityBadge $priority={notification.priority}>
                      {notification.priority}
                    </PriorityBadge>
                  </TableCell>
                  <TableCell>
                    <TargetContainer>
                      {notification.targetGroups?.map((group) => (
                        <TargetBadge key={group}>{group}</TargetBadge>
                      ))}
                    </TargetContainer>
                  </TableCell>
                  <TableCell>
                    <StatusBadge $status={notification.status}>
                      {notification.status}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{formatDate(notification.expiresAt)}</TableCell>
                  <TableCell>
                    <ActionButton 
                      onClick={() => openEditModal(notification)} 
                      $color="primary"
                      title="Edit notification"
                    >
                      <FaEdit />
                    </ActionButton>
                    <ActionButton 
                      onClick={() => handleArchive(notification.id)} 
                      $color="warning"
                      title="Archive notification"
                    >
                      <FaArchive />
                    </ActionButton>
                    <ActionButton 
                      onClick={() => handleDelete(notification.id)} 
                      $color="danger"
                      title="Delete notification"
                    >
                      <FaTrash />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </NotificationsTable>
      )}
      
      {/* Notification Add/Edit Modal */}
      {showModal && (
        <Modal>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                {editingId ? 'Edit Notification' : 'Add New Notification'}
              </ModalTitle>
              <CloseButton onClick={closeModal}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="title">Title*</Label>
                  <Input
                    type="text"
                    id="title"
                    name="title"
                    value={notificationForm.title}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="message">Message*</Label>
                  <TextArea
                    id="message"
                    name="message"
                    value={notificationForm.message}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    id="priority"
                    name="priority"
                    value={notificationForm.priority}
                    onChange={handleInputChange}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Target Groups*</Label>
                  <CheckboxGroup>
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        name="targetGroups"
                        value="all"
                        checked={notificationForm.targetGroups.includes('all')}
                        onChange={handleCheckboxChange}
                      />
                      All Users
                    </CheckboxLabel>
                    
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        name="targetGroups"
                        value="customers"
                        checked={notificationForm.targetGroups.includes('customers')}
                        onChange={handleCheckboxChange}
                      />
                      Customers
                    </CheckboxLabel>
                    
                    <CheckboxLabel>
                      <Checkbox
                        type="checkbox"
                        name="targetGroups"
                        value="partners"
                        checked={notificationForm.targetGroups.includes('partners')}
                        onChange={handleCheckboxChange}
                      />
                      Partners
                    </CheckboxLabel>
                  </CheckboxGroup>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="expiresAt">Expires At*</Label>
                  <Input
                    type="datetime-local"
                    id="expiresAt"
                    name="expiresAt"
                    value={notificationForm.expiresAt}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="actionURL">Action URL (optional)</Label>
                  <Input
                    type="url"
                    id="actionURL"
                    name="actionURL"
                    value={notificationForm.actionURL}
                    onChange={handleInputChange}
                    placeholder="e.g., /customer/plans"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="imageURL">Image URL (optional)</Label>
                  <Input
                    type="url"
                    id="imageURL"
                    name="imageURL"
                    value={notificationForm.imageURL}
                    onChange={handleInputChange}
                    placeholder="e.g., https://example.com/image.jpg"
                  />
                </FormGroup>
                
                <ModalFooter>
                  <Button type="button" onClick={closeModal}>Cancel</Button>
                  <Button type="submit" $primary>
                    {editingId ? 'Update' : 'Create'} Notification
                  </Button>
                </ModalFooter>
              </form>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </PageContainer>
  );
};

export default NotificationManagement; 
